import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, Eyebrow, EASE } from "./Reveal";
import { scrollToId } from "../lib/scroll";

const ARTWORKS = [
  {
    number: "01",
    title: "Hanuman — The Light of Devotion",
    image: "/artwork/art-hanuman.jpg",
    medium: "Oil on Canvas",
    size: '24" x 32"',
    year: "2026",
    price: "₹95,000 (India)",
    story:
      "A hand-painted artwork inspired by Lord Hanuman's unwavering devotion, strength, and spiritual energy. The glowing diya represents faith and devotion, while the warm tones create a feeling of divine light and inner peace. Entirely created by hand with careful detailing and artistic brushwork.",
  },
  {
    number: "02",
    title: "Magnificent Elephant",
    image: "/artwork/art-elephant.jpg",
    medium: "Acrylic on Canvas",
    size: '20" x 24"',
    year: "2026",
    price: "₹55,000 (India)",
    story:
      "The upward sweep of the trunk is a classical motif signifying strength, positive energy, and resilience. The calm, focused gaze of the eye, framed by deeply etched skin folds, evokes wisdom and quiet endurance.",
  },
  {
    number: "03",
    title: "Lotus",
    image: "/artwork/art-lotus.jpg",
    medium: "Acrylic on Canvas",
    size: '24" x 30"',
    year: "2026",
    price: "₹75,000 (India)",
    story:
      "A vibrant acrylic on canvas capturing pink lotus blooms in three life stages against a deep midnight-blue ground — a fully opened lotus commanding the upper right, a floating crimson-pink blossom at the lower left, and a rising bud flanked by broad green lily pads. Rippling cerulean water anchors the foreground, creating a striking contrast with the warm floral tones.",
  },
  {
    number: "04",
    title: "Fruits & Wine Still Life",
    image: "/artwork/art-stilllife.jpg",
    medium: "Oil on Canvas",
    size: '20" x 24"',
    year: "2026",
    price: "₹65,000 (India)",
    story:
      "A classic still life composition capturing the richness of fruits, the elegance of wine, and the timeless beauty of oil painting. Hand-painted with attention to light, shadow, texture, and detail.",
  },
];

function Lightbox({ artwork, onClose }) {
  const slides = [
    { type: "image", src: artwork.image },
    ...((artwork.gallery || []).map((src) => ({ type: "image", src }))),
    ...(artwork.video ? [{ type: "video", src: artwork.video }] : []),
  ];
  const [idx, setIdx] = useState(0);
  const slide = slides[idx];
  const touchX = useRef(null);

  const go = (d) => setIdx((i) => (i + d + slides.length) % slides.length);

  const onTouchStart = (e) => {
    touchX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchX.current === null || e.target.tagName === "VIDEO") return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) > 48) go(dx < 0 ? 1 : -1);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, slides.length]);

  return (
    <motion.div
      data-testid="artwork-lightbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className="fixed inset-0 z-[90] flex items-center justify-center bg-umber/90 backdrop-blur-sm sm:p-10"
    >
      <button
        data-testid="artwork-lightbox-close"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-4 top-4 z-10 rounded-full border border-cream/25 p-3.5 text-cream transition-colors hover:border-gild hover:text-gild sm:right-6 sm:top-6"
      >
        <X className="h-5 w-5" />
      </button>

      {slides.length > 1 && (
        <>
          <button
            data-testid="artwork-lightbox-prev"
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            aria-label="Previous slide"
            className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full border border-cream/25 bg-umber/60 p-3.5 text-cream backdrop-blur-sm transition-colors hover:border-gild hover:text-gild sm:left-6"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            data-testid="artwork-lightbox-next"
            onClick={(e) => { e.stopPropagation(); go(1); }}
            aria-label="Next slide"
            className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full border border-cream/25 bg-umber/60 p-3.5 text-cream backdrop-blur-sm transition-colors hover:border-gild hover:text-gild sm:right-6"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}

      <motion.figure
        initial={{ scale: 0.95, y: 24 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.96, y: 12 }}
        transition={{ duration: 0.45, ease: EASE }}
        onClick={(e) => e.stopPropagation()}
        className="max-h-full"
      >
        <div className="flex flex-col items-center bg-white p-2.5 pb-4 sm:p-3 sm:pb-5">
          <div className="relative flex min-h-[300px] w-full items-center justify-center sm:min-h-[380px]">
            <AnimatePresence mode="wait" initial={false}>
              {slide.type === "image" ? (
                <motion.img
                  key="image"
                  src={slide.src}
                  alt={artwork.title}
                  initial={{ opacity: 0, x: 60 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -60 }}
                  transition={{ duration: 0.35, ease: EASE }}
                  className="max-h-[48vh] w-auto max-w-full object-contain sm:max-h-[56vh]"
                />
              ) : (
                <motion.video
                  key="video"
                  data-testid="artwork-lightbox-video"
                  src={slide.src}
                  controls
                  autoPlay
                  muted
                  loop
                  playsInline
                  initial={{ opacity: 0, x: 60 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -60 }}
                  transition={{ duration: 0.35, ease: EASE }}
                  className="max-h-[48vh] w-full bg-black sm:max-h-[56vh]"
                />
              )}
            </AnimatePresence>
          </div>

          {slides.length > 1 && (
            <div className="mt-2 flex items-center justify-center gap-1">
              {slides.map((_, i) => (
                <button
                  key={i}
                  data-testid={`artwork-lightbox-dot-${i}`}
                  onClick={() => setIdx(i)}
                  aria-label={`Slide ${i + 1}`}
                  className="flex h-11 w-8 items-center justify-center"
                >
                  <span
                    className={`block h-2 rounded-full transition-all duration-300 ${
                      i === idx ? "w-6 bg-vermilion" : "w-2 bg-linen"
                    }`}
                  />
                </button>
              ))}
              <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.2em] text-sand">
                {idx + 1} / {slides.length}
              </span>
            </div>
          )}

          <figcaption className="mt-3 flex w-full flex-wrap items-baseline justify-between gap-2 px-1">
            <span className="font-serif text-lg italic text-ink sm:text-xl">{artwork.title}</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-sand">
              {artwork.medium} · {artwork.size} · {artwork.year}
            </span>
          </figcaption>
          <p className="mt-3 px-1 text-sm leading-relaxed text-clay">{artwork.story}</p>
        </div>
      </motion.figure>
    </motion.div>
  );
}

export default function Collection({ artworks, content }) {
  const [active, setActive] = useState(null);

  const inquire = (art) => {
    const digits = content?.whatsapp?.replace(/[^0-9]/g, "");
    if (!digits) return scrollToId("contact");
    const msg = `Hello Saivi! I'm interested in "${art.title}" (${art.medium}, ${art.size}) listed at ${art.price}. Could you share more details?`;
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  };

  return (
    <section id="collection" className="relative overflow-hidden bg-cream-deep/60 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <Reveal>
            <Eyebrow>My Art Collection</Eyebrow>
            <h2 className="mt-6 max-w-xl font-serif text-3xl tracking-tight text-ink sm:text-5xl lg:text-6xl">
              Original works, painted by hand.
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="font-mono text-[11px] uppercase tracking-[0.26em] text-sand" data-testid="collection-count">
              {(artworks?.length ? artworks.length : ARTWORKS.length).toString().padStart(2, "0")} works — 2026 — Oil &amp; Acrylic
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-10 sm:grid-cols-2 sm:gap-8 lg:grid-cols-4 lg:gap-6">
          {(artworks?.length ? artworks : ARTWORKS).map((art, i) => (
            <Reveal key={art.number} delay={i * 0.06}>
              <article data-testid={`artwork-card-${art.number}`} className="relative">
                <div className="spotlight relative">
                  <button
                    data-testid={`artwork-lightbox-trigger-${art.number}`}
                    onClick={() => setActive(art)}
                    className="mounted frame-play group block w-full cursor-zoom-in bg-white p-2.5 pb-3 text-left sm:p-3 sm:pb-4"
                  >
                    <div className="hairline-gold overflow-hidden">
                      <img
                        src={art.image}
                        alt={art.title}
                        loading="lazy"
                        className="w-full object-cover transition-transform duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]"
                      />
                    </div>
                  </button>
                  {art.sold && (
                    <span
                      data-testid={`artwork-sold-badge-${art.number}`}
                      className="absolute right-5 top-5 z-10 rotate-3 rounded-full border border-gild bg-umber/85 px-3.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.22em] text-gild shadow-lg backdrop-blur-sm"
                    >
                      Sold
                    </span>
                  )}
                </div>

                <div className="mt-4 px-1">
                  <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-vermilion">
                    Nº {art.number}
                  </p>
                  <h3 className="mt-1 font-serif text-lg leading-snug tracking-tight text-ink sm:text-xl">
                    {art.title}
                  </h3>
                  <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.16em] text-sand">
                    {art.medium} · {art.size} · {art.year}
                  </p>
                  <p className="mt-1.5 font-serif text-sm text-ink sm:text-base">{art.price}</p>
                </div>

                <div className="mt-2.5 flex h-12 items-center gap-4 border-t border-linen px-1">
                  {!art.sold && (
                    <span
                      data-testid={`artwork-available-dot-${art.number}`}
                      title="Available to buy"
                      className="flex h-full items-center"
                    >
                      <span className="h-2.5 w-2.5 rounded-full bg-green-600" />
                    </span>
                  )}
                  <button
                    data-testid={`artwork-inquire-button-${art.number}`}
                    onClick={() => inquire(art)}
                    className="group flex min-h-[48px] items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-ink"
                  >
                    <span className="border-b border-vermilion pb-0.5 transition-colors group-hover:text-vermilion">
                      Inquire
                    </span>
                    <ArrowUpRight className="h-3.5 w-3.5 text-vermilion transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                  <button
                    data-testid={`artwork-inspect-button-${art.number}`}
                    onClick={() => setActive(art)}
                    className="flex h-full items-center font-mono text-[10px] uppercase tracking-[0.22em] text-sand transition-colors hover:text-gild"
                  >
                    Inspect
                  </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && <Lightbox artwork={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </section>
  );
}
